import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { debounce } from "lodash";

import Modal from "./modal";
import api from "../services/api";
import { HiTrash } from "react-icons/hi2";
import { FaPlus } from "react-icons/fa";

//https://dribbble.com/shots/21132444-Manage-team-members-modal

export default function UserPicker({ value, onChange, roles = ["admin", "user", "super_admin"], projectId }) {
  return (
    <div>
      <div className="flex items-center w-full gap-2 flex-wrap">
        {(value || []).map((user) => (
          <button
            key={user.user_id}
            onClick={() => {
              const updatedUsers = value.filter((u) => u.user_id !== user.user_id);
              onChange(updatedUsers);
            }}
            className="relative hover:ring-2 hover:ring-red-700 rounded-full group">
            <img className="h-10 w-10 rounded-full" src={user.user_avatar} alt={user.user_name} />
            <div className="absolute inset-0 bg-red-700/30 text-white rounded-full group-hover:flex hidden items-center justify-center">
              <HiTrash className="w-4 h-4" />
            </div>
          </button>
        ))}
        <SearchModal
          selectedUsers={value || []}
          onUser={(user) => {
            if (value) {
              if (!value.some((u) => u.user_id === user._id)) {
                onChange([...value, { user_id: user._id, user_name: user.name, user_avatar: user.avatar }]);
              }
            } else {
              onChange([{ user_id: user._id, user_name: user.name, user_avatar: user.avatar }]);
            }
          }}
          roles={roles}
          projectId={projectId}
        />
      </div>
    </div>
  );
}
const SearchModal = ({ onUser, roles, projectId, selectedUsers }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [initialUsers, setInitialUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const currentUser = useSelector((state) => state.Auth.user);

  const fetchInitialUsers = async () => {
    if (!open) return;
    setLoading(true);
    let projectUsers = [];
    if (projectId) {
      try {
        const res = await api.post(`/user/search`, { project_ids: [projectId], status: "active", per_page: 100 });
        if (res.ok) {
          projectUsers = res.data.users;
        } else {
          console.error("Failed to fetch project users:", res.error);
        }
      } catch (error) {
        console.error("Error fetching project users:", error);
      }
    }

    const combinedUsers = [{ ...currentUser, name: `${currentUser?.name} (me)` }, ...projectUsers].filter(
      (user, index, self) => user?._id && index === self.findIndex((u) => u._id === user._id) && !selectedUsers.some((su) => su.user_id === user._id),
    );

    setInitialUsers(combinedUsers);
    setUsers(combinedUsers);
    setLoading(false);
  };

  useEffect(() => {
    fetchInitialUsers();
  }, [open, projectId, currentUser, selectedUsers]);

  const debouncedSearchUser = debounce(async (searchQuery) => {
    setLoading(true);
    const res = await api.post("/user/search", { search: searchQuery, per_page: 10, sort: "name", roles });
    setUsers(res.data.users);
    setLoading(false);
  }, 300);

  useEffect(() => {
    if (search.length > 0) {
      debouncedSearchUser(search);
    } else {
      setUsers(initialUsers.filter((u) => !selectedUsers.some((su) => su.user_id === u._id)));
    }
  }, [search, initialUsers, selectedUsers]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 border border-gray-300 flex items-center gap-1">
        <FaPlus className="w-3 h-3" />
        Add a user
      </button>
      <Modal
        isOpen={open}
        onClose={() => {
          setOpen(false);
          setSearch("");
          setUsers([]);
          setInitialUsers([]);
        }}
        className="w-1/3">
        <div className="p-4 m-4">
          <input type="text" placeholder="Search user" className="border border-gray-300 p-2 rounded w-full mb-4" value={search} onChange={(e) => setSearch(e.target.value)} />
          {loading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {users.length === 0 && search && <div className="text-center text-gray-500">No users found.</div>}
              {users.length === 0 && !search && initialUsers.length === 0 && <div className="text-center text-gray-500">No users to show.</div>}
              {users.map((user) => {
                return (
                  <Card
                    key={user._id}
                    user={user}
                    onClick={() => {
                      onUser(user);
                      setOpen(false);
                      setSearch("");
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

const Card = ({ user, onClick }) => {
  return (
    <div onClick={onClick} className="bg-white border border-gray-200 rounded-md p-2 cursor-pointer hover:bg-gray-100 transition-colors duration-150 ease-in-out">
      <div className="flex items-center gap-3">
        <img className="h-8 w-8 rounded-full object-cover" src={user.avatar || "/path/to/default/avatar.png"} alt={user.name} />
        <div className="flex-1 overflow-hidden">
          <p className="font-medium text-sm truncate">{user.name}</p>
          {user.contract && <span className="text-gray-500 text-xs">{user.contract}</span>}
        </div>
      </div>
    </div>
  );
};
